import { useState } from 'react';
import { Navigation, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const RADIUS_OPTIONS = [
  { value: '10', label: '10 km' },
  { value: '25', label: '25 km' },
  { value: '50', label: '50 km' },
  { value: '100', label: '100 km' },
  { value: '250', label: '250 km' },
];

const NearbyButton = ({ onLocationChange, onRadiusChange, className = '' }) => {
  const [loading, setLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [radius, setRadius] = useState('25');
  const [showControls, setShowControls] = useState(false);

  const requestLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setLoading(true);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setUserLocation(location);
        setShowControls(true);
        setLoading(false);
        
        if (onLocationChange) {
          onLocationChange(location);
        }
        if (onRadiusChange) {
          onRadiusChange(parseInt(radius));
        }
        
        toast.success('Location found! Showing nearby businesses.');
      },
      (error) => {
        setLoading(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error('Location permission denied. Please enable location access.');
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Location information unavailable.');
            break;
          case error.TIMEOUT:
            toast.error('Location request timed out.');
            break;
          default:
            toast.error('Unable to get your location.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // Cache for 5 minutes
      }
    );
  };

  const handleRadiusChange = (newRadius) => {
    setRadius(newRadius);
    if (onRadiusChange) {
      onRadiusChange(parseInt(newRadius));
    }
  };

  const clearLocation = () => {
    setUserLocation(null);
    setShowControls(false);
    if (onLocationChange) {
      onLocationChange(null);
    }
    if (onRadiusChange) {
      onRadiusChange(null);
    }
    toast.success('Location filter cleared');
  };

  if (showControls && userLocation) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="flex items-center gap-2 bg-primary/10 px-3 py-1.5 rounded-full">
          <Navigation className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium text-primary">Near Me</span>
        </div>
        
        <Select value={radius} onValueChange={handleRadiusChange}>
          <SelectTrigger className="w-[100px] h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RADIUS_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={clearLocation}
          className="h-8 w-8 p-0"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  return (
    <Button
      variant="outline"
      onClick={requestLocation}
      disabled={loading}
      className={`gap-2 ${className}`}
      data-testid="nearby-btn"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Navigation className="w-4 h-4" />
      )}
      Near Me
    </Button>
  );
};

export default NearbyButton;
